import { FilterParam } from 'common/src/query-params/FilterParam';
import {
  ArrayOperators,
  SingleOperators,
} from 'common/src/query-params/operators';
import {
  AlertSubtype,
  hiddenAlertSubtypes,
  requireResolutionSubtypes,
  visibleTypeToSubtypeAlertMap,
} from 'common/src/types/enums/AlertSubtype.enum';
import type { IUserAlertPreferences } from 'common/src/types/interfaces';

import {
  alertTypes,
  buildInitialPreferences,
  getPreferenceKey,
} from 'portal/src/components/systemAdministration/Form/AlertPreferences/helpers';

const requireResolutionSubtypeSet = new Set(requireResolutionSubtypes);

export type AlertResolutionStatus = '-' | 'No' | 'Yes';

export const ALERT_RESOLUTION_STATUS_OPTIONS: [
  AlertResolutionStatus,
  AlertResolutionStatus,
  AlertResolutionStatus,
] = ['-', 'No', 'Yes'];

export const isResolutionRequiredSubtype = (
  subtype?: AlertSubtype | null,
): subtype is AlertSubtype => {
  return !!subtype && requireResolutionSubtypeSet.has(subtype);
};

export const getAlertResolutionStatus = (
  subtype?: AlertSubtype | null,
  resolved?: boolean | null,
): AlertResolutionStatus => {
  if (!isResolutionRequiredSubtype(subtype)) {
    return '-';
  }

  return resolved ? 'Yes' : 'No';
};

export const getAlertResolutionFilters = (
  status: AlertResolutionStatus,
): FilterParam[] => {
  if (status === '-') {
    return [
      {
        field: 'subtype',
        operator: ArrayOperators.NOT_IN,
        value: requireResolutionSubtypes,
      },
    ];
  }

  return [
    {
      field: 'subtype',
      operator: ArrayOperators.IN,
      value: requireResolutionSubtypes,
    },
    {
      field: 'resolved',
      operator: SingleOperators.EQUALS,
      value: status === 'Yes',
    },
  ];
};

export const getSubtypeFilterOptions = (
  subtypes: AlertSubtype[],
): Array<{ value: AlertSubtype; label: AlertSubtype }> => {
  return [...subtypes]
    .filter((subtype) => !hiddenAlertSubtypes.has(subtype))
    .sort((a: AlertSubtype, b: AlertSubtype) => a.localeCompare(b))
    .map((subtype: AlertSubtype) => ({ value: subtype, label: subtype }));
};

export function getEnabledAlertSubtypesAndFilter(
  preferences: IUserAlertPreferences[] = [],
): { enabledSubtypes: AlertSubtype[]; subtypeFilter: FilterParam } {
  // Uses the same defaults as the alert preferences form, so subtypes without
  // a saved preference count as enabled
  const preferenceState = buildInitialPreferences(preferences);
  const enabledSubtypes = [
    ...new Set(
      alertTypes.flatMap((type) =>
        visibleTypeToSubtypeAlertMap[type].filter(
          (subtype) => preferenceState[getPreferenceKey(type, subtype)].enabled,
        ),
      ),
    ),
  ];

  return {
    enabledSubtypes,
    subtypeFilter: {
      field: 'subtype',
      operator: ArrayOperators.IN,
      value: enabledSubtypes,
    },
  };
}
